import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, ReplaySubject, Observable, of } from 'rxjs';
import { User } from 'app/models';
import { environment } from '../../../environments/environment';
import { switchMap, takeUntil, catchError } from 'rxjs/operators';

@Injectable()
export class UsersSearchService implements OnDestroy {
  readonly internalSearchUrl = `${environment.apiUrl}/api/v2/Users`;

  private _request: Subject<any> = new Subject();
  private _users: ReplaySubject<User[]> = new ReplaySubject<User[]>();
  public users$: Observable<any> = this._users.asObservable();
  private _error: ReplaySubject<string> = new ReplaySubject<string>();
  public error$: Observable<string> = this._error.asObservable();

  public usersSearchString = '';
  public internalOnly = false;
  public appId;
  public isInternal = true;
  public url: string;
  private onDestroy$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService
  ) {
    this._request
      .pipe(
        switchMap(() => this.getData().pipe(
            catchError(error =>
              this._handleSearchError(error, this._translateService)
            )
          ))
      )
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);
          this._users.next(data as User[]);
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  searchUsers(
    query: string,
    internalOnly: boolean = false,
    appId: string = '',
    isInternal: boolean = true
  ): void {
    this.isInternal = isInternal;
    this.usersSearchString = query;
    this.internalOnly = internalOnly;
    this.appId = appId;
    if (this.isInternal) {
      this.url = `${this.internalSearchUrl}?name=${this.usersSearchString}&internalonly=${this.internalOnly}&appId=${this.appId}`;
    }

    this._request.next(null);
  }

  private getData(): Observable<any> {
    if (this.usersSearchString) {
      return this._http.get(this.url);
    } else {
      return of([]);
    }
  }

  private _handleSearchError(
    error: HttpErrorResponse,
    translateService: TranslateService
  ) {
    const data: User[] = [];
    this._users.next(data as User[]);
    if (error.status === 404) {
      return of({ errorMessage: '404' });
    }
    let errorMsg = '';
    translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[users-search-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[users-search-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    return of({ errorMessage: errorMsg });
  }
}
